import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SPACES as DEFAULT_SPACES, resolveImage } from "../../lib/data";
import { usePageContent } from "../../lib/usePageContent";

const PAGE_DEFAULTS = {
  spaces_eyebrow: "Spazi & Servizi",
  spaces_title: "Non solo hotel.",
  spaces_title_accent: "Ambienti versatili.",
  spaces_description:
    "Dalla terrazza panoramica del Glam Hotel al JLounge del J24, fino alle sale meeting modulari. I nostri hotel sono luoghi dove lavorare, incontrare, celebrare momenti importanti.",
  spaces: DEFAULT_SPACES,
};

export default function SpacesSection() {
  const c = usePageContent("home", PAGE_DEFAULTS);
  const [active, setActive] = useState(0);

  // Merge CMS spaces with DEFAULT_SPACES so missing images fall back gracefully
  const spaces =
    Array.isArray(c.spaces) && c.spaces.length
      ? c.spaces.map((s, i) => ({
          ...DEFAULT_SPACES[i],
          ...s,
          image: s.image || DEFAULT_SPACES[i]?.image || "",
        }))
      : DEFAULT_SPACES;

  const current = spaces[Math.min(active, spaces.length - 1)];

  return (
    <section
      data-testid="spaces-section"
      className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto"
    >
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            {c.spaces_eyebrow || PAGE_DEFAULTS.spaces_eyebrow}
          </div>
          <h2 className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl tracking-tightest">
            {c.spaces_title || PAGE_DEFAULTS.spaces_title}
            <br />
            <span className="text-[#C48F56]">
              {c.spaces_title_accent || PAGE_DEFAULTS.spaces_title_accent}
            </span>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-white/60 leading-relaxed">
            {c.spaces_description || PAGE_DEFAULTS.spaces_description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {spaces.map((s, i) => (
          <button
            key={s.key || i}
            data-testid={`spaces-tab-${s.key || i}`}
            onClick={() => setActive(i)}
            className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${
              active === i
                ? "bg-white text-black border-white"
                : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/9] md:aspect-[21/9]">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.key || active}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 bg-cover bg-center bg-[#0F0F14]"
            style={{
              backgroundImage: current.image
                ? `url(${resolveImage(current.image)})`
                : undefined,
            }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key || active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="font-display text-4xl md:text-5xl tracking-tightest">
                {current.title}
              </h3>
              <p className="mt-4 text-white/80 text-lg leading-relaxed">
                {current.text}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
